import {
  authorizationEndpoint,
  blacklistPermissions,
  handleAccessRequestEndpoint,
  permissionsEndpoint,
  requestedAssignmentsEndpoint,
  userAccessRequestEndpoint,
} from '../constants';
import { Assignment } from '../global-state/types';
import { homeTranslations, sideMenuTranslations } from '../translations';
import { areaOverviewTranslations } from '../translations/areaOverview';
import { useAuthAxiosRequest } from './useAuthAxiosRequest';
import { useUser } from './useController';

export const permissionTypes = [
  // PAGES
  'transactionSearchPage',
  'employeesAndRolesPage',
  'kpiTargetsPage',
  'chatBoxPage',
  'profitAndLossPage',
  'salesAnalysisPage',
  'soldProductsPage',
  'storeInventoryCount',
  'productIndexSubindex',
  'areaResultPage',
  'areaOverview', // FE only

  // WIDGETS
  'shareOfReceiptWidget',
  'topSellingGarmentsWidget',
  'salesPerformanceWidget',
  'conversionRateWidget',
  'avgPiecesPerReceiptWidget',
  'avgPurchasePerReceiptWidget',
  'newMembersWidget',
  'areaComparisonWidget',
  'customerFeedBackWidget',
  'queueLengthWidget',
  'queueLengthTillViewWidget',
  'orisCustomerWidget',
  'rfidApprovalsWidget',
  'rfidSalesWidget',
  'efficiencyWidget',
  'customerEngagementWidget',
  'stockCountWidget',
  'newAppCustomersWidget',
  'salesInsightWidget',
  'stockAccuracyWidget',
  'helpASaleWidget',
  'bopisWidget',
  'financialPlanned',
  'financialBudget',

  // NEW STORE WIDGETS
  'salesOverviewPage',

  // OVERVIEW WIDGETS
  'conversionRateOverview',
  'membersOverview',
  'salesOverview',
  'efficiencyOverview',
  'rfidOverview',
  'queueAndCustomerOverview',
  'budgetAndPlanOverview',
  'kpiTargetsOverview',
  'getCountsOverview', // 'SummaryWidgetsOverview',

  // SETTINGS
  'avatarManagementPage',
  'highlightWidget',
  'personalSettings',
  'kpiTargets',
  'areaCaps',
  'lockTargets',
  'employeePermissions',
  'blacklistPermissionsForArea',
  'scoShareOfReceipt',
  'currencyConverter',
  'assetManagementWidget',
] as const;

export const permissionGroupings = {
  pages: [
    'transactionSearchPage',
    'employeesAndRolesPage',
    'kpiTargetsPage',
    'profitAndLossPage',
    'salesAnalysisPage',
    'soldProductsPage',
    'areaResultPage',
    'storeInventoryCount',
    'productIndexSubindex',
    'blacklistPermissionsForArea',
    'areaOverview', // FE only
  ] as UserPermissionType[],

  widgets: [
    'shareOfReceiptWidget',
    'topSellingGarmentsWidget',
    'salesPerformanceWidget',
    'conversionRateWidget',
    'avgPiecesPerReceiptWidget',
    'avgPurchasePerReceiptWidget',
    'newMembersWidget',
    'areaComparisonWidget',
    'customerFeedBackWidget',
    'queueLengthWidget',
    'queueLengthTillViewWidget',
    'orisCustomerWidget',
    'rfidApprovalsWidget',
    'rfidSalesWidget',
    'efficiencyWidget',
    'customerEngagementWidget',
    'stockCountWidget',
    'newAppCustomersWidget',
    'salesInsightWidget',
    'stockAccuracyWidget',
    'helpASaleWidget',
    'bopisWidget',
    'scoShareOfReceipt',
    'financialPlanned',
    'financialBudget',
  ] as UserPermissionType[],

  overviewWidgets: [
    'conversionRateOverview',
    'membersOverview',
    'salesOverview',
    'efficiencyOverview',
    'rfidOverview',
    'queueAndCustomerOverview',
    'budgetAndPlanOverview',
    'kpiTargetsOverview',
    'getCountsOverview', // 'SummaryWidgetsOverview',
  ] as UserPermissionType[],

  settings: [
    'avatarManagementPage',
    'highlightWidget',
    'chatBoxPage',
    'personalSettings',
    'kpiTargets',
    'areaCaps',
    'lockTargets',
    'employeePermissions',
    'currencyConverter',
  ] as UserPermissionType[],
};

export const getPermissionTranslationData = (
  permissionType: UserPermissionType,
): { key: string; defaultValue: string } | null => {
  switch (permissionType) {
    // Pages
    case 'transactionSearchPage':
      return sideMenuTranslations.transaction;
    case 'employeesAndRolesPage':
      return sideMenuTranslations.employees;
    case 'kpiTargetsPage':
      return sideMenuTranslations.kpiTarget;
    case 'profitAndLossPage':
      return sideMenuTranslations.profitAndLoss;
    case 'salesAnalysisPage':
      return sideMenuTranslations.salesAnalysis;
    case 'soldProductsPage':
      return sideMenuTranslations.soldProducts;
    case 'areaResultPage':
      return sideMenuTranslations.areaResult;
    case 'storeInventoryCount':
      return sideMenuTranslations.yearlyStockTake;
    case 'productIndexSubindex':
      return sideMenuTranslations.productCategory;
    case 'blacklistPermissionsForArea':
      return sideMenuTranslations.widgetAccessControl;
    case 'areaOverview':
      return sideMenuTranslations.areaOverview;

    // Widgets
    case 'shareOfReceiptWidget':
      return homeTranslations.shareOfReceipts.title;
    case 'topSellingGarmentsWidget':
      return homeTranslations.topSelling.title;
    case 'salesPerformanceWidget':
      return homeTranslations.hourlyTurnover.title;
    case 'conversionRateWidget':
      return homeTranslations.conversionRate.title;
    case 'avgPiecesPerReceiptWidget':
      return homeTranslations.piecesPerReceipt.title;
    case 'avgPurchasePerReceiptWidget':
      return homeTranslations.averagePurchase.title;
    case 'newMembersWidget':
      return homeTranslations.newMembers.title;
    case 'areaComparisonWidget':
      return homeTranslations.areaComparison.title;
    case 'customerFeedBackWidget':
      return homeTranslations.customerFeedback.title;
    case 'queueLengthWidget':
      return homeTranslations.queueLength.title;
    case 'queueLengthTillViewWidget':
      return homeTranslations.queueLength.queueLengthTillView;
    case 'orisCustomerWidget':
      return homeTranslations.orisCustomers.title;
    case 'rfidApprovalsWidget':
      return homeTranslations.rfidApprovals.title;
    case 'rfidSalesWidget':
      return homeTranslations.rfidSalesRetagging.title;
    case 'efficiencyWidget':
      return homeTranslations.efficiencyWidget.title;
    case 'customerEngagementWidget':
      return homeTranslations.customerEngagement.title;
    case 'stockCountWidget':
      return homeTranslations.rfidCount.title;
    case 'newAppCustomersWidget':
      return homeTranslations.appCustomers.title;
    case 'salesInsightWidget':
      return homeTranslations.salesInsightWidget.title;
    case 'stockAccuracyWidget':
      return homeTranslations.stockAccuracy.title;
    case 'helpASaleWidget':
      return homeTranslations.helpASale.title;
    case 'bopisWidget':
      return homeTranslations.bopis.title;
    case 'scoShareOfReceipt':
      return homeTranslations.scoShareOfReceipts.title;
    case 'financialPlanned':
      return homeTranslations.settingsPermissions.financialPlanned;
    case 'financialBudget':
      return homeTranslations.settingsPermissions.financialBudget;

    // area widgets
    case 'conversionRateOverview':
      return areaOverviewTranslations.conversionRateOverview.title;
    case 'membersOverview':
      return areaOverviewTranslations.memberOverview.title;
    case 'salesOverview':
      return areaOverviewTranslations.salesOverview.title;
    case 'efficiencyOverview':
      return areaOverviewTranslations.efficiencyOverview.title;
    case 'rfidOverview':
      return areaOverviewTranslations.rfidOverview.title;
    case 'queueAndCustomerOverview':
      return areaOverviewTranslations.queueCustomerOverview.title;
    case 'budgetAndPlanOverview':
      return areaOverviewTranslations.budgetPlanOverview.title;
    case 'kpiTargetsOverview':
      return areaOverviewTranslations.kpiOverview.title;
    case 'getCountsOverview':
      return areaOverviewTranslations.summaryWidgetsOverview.title;

    // Settings
    case 'avatarManagementPage':
      return sideMenuTranslations.avatarManagement;
    case 'chatBoxPage':
      return sideMenuTranslations.chatBoxPage;
    case 'personalSettings':
      return homeTranslations.settingsPermissions.personalSettings;
    case 'kpiTargets':
      return homeTranslations.settingsPermissions.kpiTargets;
    case 'areaCaps':
      return homeTranslations.settingsPermissions.areaCaps;
    case 'lockTargets':
      return homeTranslations.settingsPermissions.lockTargets;
    case 'employeePermissions':
      return homeTranslations.settingsPermissions.employeePermissions;
    case 'currencyConverter':
      return homeTranslations.settingsPermissions.currencyConverter;
    case 'highlightWidget':
      return homeTranslations.highlight.title;
    default:
      return null;
  }
};

export type UserPermissionType = (typeof permissionTypes)[number];

export type PermissionItemsMap = {
  [key in UserPermissionType]: string;
};

export type GetBlacklistPermissionsResponse = {
  areaCode: string;
  permissions: UserPermissionType[];
};

export type PostBlacklistPermissionsRequest = {
  permissions: UserPermissionType[];
};

export type UserAccessRequest = {
  jobRoleCode: string;
  countryCode: string;
  organizationId: string;
  organizationName: string;
  businessUnitId?: string;
  businessUnitName?: string;
  brandId: number;
};

export type RequestStatusType = 'Pending' | 'Approved' | 'Rejected' | 'Expired';

export type AccessRequestItem = {
  id: string;
  upn: string;
  requestTimestamp: string;
  statusTimestamp: string;
  status: RequestStatusType;
  requestedAssignment: Assignment;
};

export type AccessRequestDetails = {
  requests: AccessRequestItem[];
};

export const useUserPermissions = () => {
  const {
    get: { currentStoreId, profile },
  } = useUser();

  const { authAxiosRequest } = useAuthAxiosRequest();

  const fetchUserPermissions = async (upn: string, storeId: string) => {
    const roleMatrixResponse = await authAxiosRequest<PermissionItemsMap>(
      `${authorizationEndpoint}${permissionsEndpoint}/${upn}`,
      {
        params: { storeId },
      },
    );

    return roleMatrixResponse;
  };

  const getBlacklistPermissions = async () => {
    const response = await authAxiosRequest<GetBlacklistPermissionsResponse>(
      `${authorizationEndpoint}${blacklistPermissions}/${currentStoreId}`,
    );

    return response;
  };

  const postBlacklistPermissions = async (request: PostBlacklistPermissionsRequest) => {
    const roleMatrixResponse = await authAxiosRequest<GetBlacklistPermissionsResponse>(
      `${authorizationEndpoint}${blacklistPermissions}/${currentStoreId}`,
      {
        method: 'POST',
        data: request,
      },
    );

    return roleMatrixResponse;
  };

  const userAccessRequest = async (request: UserAccessRequest) => {
    const response = await authAxiosRequest<UserAccessRequest>(
      `${authorizationEndpoint}/${profile.upn}${userAccessRequestEndpoint}`,
      {
        method: 'POST',
        data: request,
      },
    );
    return response;
  };

  const fetchAccessRequest = async () => {
    const fetchAccessResponse = await authAxiosRequest<AccessRequestDetails>(
      `${authorizationEndpoint}${userAccessRequestEndpoint}`,
    );

    return fetchAccessResponse;
  };

  const updateAccessRequest = async (requestId: string, status: RequestStatusType) => {
    const request = {
      status: status,
    };
    const response = await authAxiosRequest<AccessRequestItem>(
      `${authorizationEndpoint}/${requestId}${handleAccessRequestEndpoint}`,
      {
        method: 'PATCH',
        data: request,
      },
    );

    return response;
  };

  const fetchRequestedAssignments = async () => {
    const response = await authAxiosRequest<AccessRequestDetails>(
      `${authorizationEndpoint}${requestedAssignmentsEndpoint}`,
    );
    return response;
  };

  return {
    fetchUserPermissions,
    getBlacklistPermissions,
    postBlacklistPermissions,
    userAccessRequest,
    fetchAccessRequest,
    updateAccessRequest,
    fetchRequestedAssignments,
  };
};
